import { useEffect, useState } from "react";
import { Mail, Phone, Star } from "lucide-react";
import adminApi from "@/admin/lib/adminApi";
import MerchantDetailSheet from "@/admin/components/MerchantDetailSheet";

/**
 * AdminFeedback
 * Lists merchant feedback/ratings for the admin panel — worst-first by
 * default, so you can spot merchants to follow up with immediately.
 *
 * USAGE: drop this into your admin router next to AdminMerchants /
 * AdminCoupons, e.g. at /admin/feedback, wrapped in your existing
 * AdminLayout.
 *
 * Expects a POST /admin/feedback endpoint (see
 * n8n-admin-feedback-list.json) returning an array of:
 *   { id, business_id, business_name, contact_name, contact_email,
 *     contact_phone, plan, rating, comment, page_context, created_at }
 */

const FILTERS = [
  { key: "all", label: "All ratings", max_rating: null },
  { key: "low", label: "1–2 stars", max_rating: 2 },
  { key: "mid", label: "1–3 stars", max_rating: 3 },
];

function RatingStars({ rating }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          className={n <= rating ? "fill-hotpink text-hotpink" : "text-ink/15"}
        />
      ))}
    </span>
  );
}

export default function AdminFeedback() {
  const [filter, setFilter] = useState("low"); // default to what needs attention
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailMerchantId, setDetailMerchantId] = useState(null);
  const limit = 20;

  useEffect(() => {
    setLoading(true);
    const activeFilter = FILTERS.find((f) => f.key === filter);
    adminApi
      .post("/admin/feedback", {
        max_rating: activeFilter.max_rating,
        page,
        limit,
      })
      .then((res) => setItems(res.data || []))
      .finally(() => setLoading(false));
  }, [filter, page]);

  return (
    <>
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="admin-feedback-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Merchant feedback</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
            Ratings from the in-app check-in
          </h2>
          <p className="mt-1 text-sm text-ink/60">
            Sorted worst-first — low ratings are the ones worth a follow-up.
          </p>
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setPage(1); }}
              data-testid={`feedback-filter-${f.key}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                filter === f.key ? "bg-ink text-cream" : "border border-ink/15 bg-white text-ink/60 hover:text-ink"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="py-8 text-center text-sm text-ink/50">Loading…</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink/50">No feedback in this range yet.</p>
        ) : (
          items.map((f) => (
            <div
              key={f.id}
              className="rounded-2xl border border-ink/10 p-4"
              data-testid={`feedback-row-${f.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <button
                    type="button"
                    onClick={() => setDetailMerchantId(f.business_id)}
                    data-testid={`feedback-open-merchant-${f.id}`}
                    className="font-semibold text-ink underline decoration-ink/20 underline-offset-2 hover:text-hotpink hover:decoration-hotpink"
                  >
                    {f.business_name}
                  </button>
                  {f.ig_username && (
                    <p className="text-xs text-ink/50">@{f.ig_username}</p>
                  )}
                  <div className="mt-1"><RatingStars rating={f.rating} /></div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Submitted</p>
                  <p className="mt-0.5 text-xs text-ink/60">
                    {new Date(f.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {f.comment && (
                <p className="mt-3 rounded-xl bg-ink/[0.03] px-3 py-2.5 text-sm leading-relaxed text-ink/80">
                  "{f.comment}"
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-ink/5 pt-3 text-xs">
                {f.contact_email && (
                  <a
                    href={`mailto:${f.contact_email}?subject=${encodeURIComponent(
                      `Following up on your CheckoutDM feedback`
                    )}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 font-semibold text-ink hover:bg-ink/5"
                  >
                    <Mail size={12} /> {f.contact_email}
                  </a>
                )}
                {f.contact_phone && (
                  <a
                    href={`tel:${f.contact_phone}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 font-semibold text-ink hover:bg-ink/5"
                  >
                    <Phone size={12} /> {f.contact_phone}
                  </a>
                )}
                {!f.contact_email && !f.contact_phone && (
                  <span className="text-ink/30">No contact info on file for this merchant.</span>
                )}
                <span className="ml-auto rounded-full bg-ink/[0.04] px-2.5 py-1 font-semibold uppercase tracking-wide text-ink/50">
                  {f.plan || "—"} plan
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-ink/10 pt-4 text-sm text-ink/70">
        <span>Page {page}</span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            disabled={items.length < limit}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <MerchantDetailSheet
      open={!!detailMerchantId}
      onOpenChange={(v) => !v && setDetailMerchantId(null)}
      merchantId={detailMerchantId}
    />
    </>
  );
}
